import * as React from "react"
import type { LucideIcon } from "lucide-react"

import { cn } from "@/lib/utils"

// ============================================================================
// Component: EmptyState
//
// Shared "nothing here yet" body for dashboard cards — icon + centered
// message, filling whatever height its flex parent gives it. Matches the
// Figma empty-state treatment (2026-10-02): a muted icon above one or two
// lines of muted text, vertically centered in the card rather than
// top-aligned like a loading/error line.
//
// Usage: the card's own wrapper needs `flex h-full flex-col` and its
// CardContent needs `flex-1` (and usually `flex flex-col`) so this component
// actually has room to center in — see TopModelsUsed.tsx for the pattern.
// ============================================================================

interface EmptyStateProps {
  icon: LucideIcon
  message: React.ReactNode
  className?: string
}

export function EmptyState({ icon: Icon, message, className }: EmptyStateProps) {
  return (
    <div
      className={cn(
        "flex flex-1 flex-col items-center justify-center gap-3 px-6 py-10 text-center",
        className
      )}
    >
      <Icon className="h-8 w-8 text-muted-foreground/40" aria-hidden="true" strokeWidth={1.5} />
      <p className="text-sm text-muted-foreground">{message}</p>
    </div>
  )
}
